import { createElement } from 'react';

import { isPermission, isSystemRoleKey, type Permission } from '@bookora/shared';
import { TrackingCode, errorMessageFa, isApiError } from '@bookora/ui';
import type { MessageInstance, ModalHookApi, NotificationInstance } from '@bookora/ui';

import { useSessionStore } from '@/auth/token-store';

import { permissionCopy } from './reasons';

/**
 * Toasts and dialogs from anywhere, still themed.
 *
 * AntD v5's static `message.success(...)` renders OUTSIDE the `ConfigProvider`
 * and therefore ignores the Bookora theme, the RTL direction and the Persian
 * locale — it comes out as a left-aligned default-blue toast in the middle of a
 * rose RTL panel. The supported fix is `App.useApp()`, which is a hook and so
 * unavailable to a store action or an interceptor.
 *
 * `FeedbackBridge` (in `app/providers.tsx`) registers the hook instances here
 * once, and these functions forward to them. Before registration — which is
 * only the first tick, and in unit tests that render a component in isolation —
 * calls are dropped rather than falling back to the unthemed statics.
 *
 * ── WHAT A FAILURE TOAST ALSO DOES ──────────────────────────────────────────
 *
 *  · A refusal for want of a permission (`PERMISSION_DENIED`, whose `details`
 *    name the missing key) says WHICH permission and what to do, by the one
 *    role-aware policy (`permissionCopy`, critic C18) — a custom role is told
 *    where the owner grants it, a system role that it is not possible with this
 *    role. The server's generic sentence named neither.
 *  · The failure's tracking code is kept (`lastApiError`) — code, request id,
 *    page and time, never the message — so «تماس با پشتیبانی» can offer it
 *    after the toast has faded; `errorBanner` shows it outright.
 *  · Listeners (`onErrorShown`) hear the code, for the struggle detector: the
 *    same refusal twice on a page is a person who is stuck.
 *  · Every failed mutation reaches the last two as well (`noteApiFailure`,
 *    from the query cache), once per error, however the screen shows it.
 */

interface FeedbackApi {
  message: MessageInstance;
  modal: ModalHookApi;
  notification: NotificationInstance;
}

let api: FeedbackApi | null = null;

export function registerFeedbackApi(next: FeedbackApi): void {
  api = next;
}

/** Test seam. */
export function resetFeedbackApi(): void {
  api = null;
  lastError = null;
  listeners.clear();
  reported = new WeakSet<object>();
}

/* ── The last failure, for support ──────────────────────────────────────── */

export interface LastApiError {
  requestId: string;
  code: string;
  pathname: string;
  /** Epoch ms. */
  at: number;
}

let lastError: LastApiError | null = null;

/** The last failure shown to the user that carried a tracking code, or `null`. */
export function lastApiError(): LastApiError | null {
  return lastError;
}

// Another clinic's failure is not this clinic's support question.
useSessionStore.subscribe((state, prev) => {
  if (state.activeClinicId !== prev.activeClinicId) lastError = null;
});

/* ── Who hears about a failure ──────────────────────────────────────────── */

export interface ShownError {
  /** The stable `ErrorCode` (or a transport code). Never the message. */
  code: string;
  statusCode: number;
}

type ErrorListener = (error: ShownError) => void;
const listeners = new Set<ErrorListener>();

/**
 * Subscribes to every failure a toast or banner shows, and every failed
 * mutation (`noteApiFailure`) — each error once. Returns the unsubscribe.
 */
export function onErrorShown(listener: ErrorListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Failures already reported: a failed save reaches here twice — through the
 * query cache's `onError` and through the toast its screen shows — and must
 * count once, or one refusal would look like «the same refusal twice».
 */
let reported = new WeakSet<object>();

function noteShown(error: unknown): void {
  if (!isApiError(error)) return;
  if (reported.has(error)) return;
  reported.add(error);
  if (error.requestId) {
    lastError = {
      requestId: error.requestId,
      code: error.code,
      pathname: typeof window === 'undefined' ? '' : window.location.pathname,
      at: Date.now(),
    };
  }
  for (const listener of listeners) {
    try {
      listener({ code: error.code, statusCode: error.statusCode });
    } catch {
      // A listener's failure never costs the user the toast.
    }
  }
}

/**
 * A save failed, whatever the screen then does with it. Wired to the query
 * cache's `onError` (`app/providers.tsx`): most screens report a failure
 * through the kit's `useApiFeedback().showError`, which this module never
 * sees, so without this tap the tracking code and the struggle detector's
 * «same refusal twice» would miss most of the panel. Idempotent per error:
 * the toast that follows is not a second failure.
 */
export function noteApiFailure(error: unknown): void {
  noteShown(error);
}

/* ── The words ──────────────────────────────────────────────────────────── */

function missingPermission(error: unknown): Permission | null {
  if (!isApiError(error) || error.code !== 'PERMISSION_DENIED') return null;
  const details = error.details as { missing?: unknown } | null | undefined;
  const missing: unknown[] = Array.isArray(details?.missing) ? details.missing : [];
  return missing.find(isPermission) ?? null;
}

/**
 * The Persian sentence for a failure: for a permission refusal, the named
 * permission and the next step for THIS role; otherwise the server's own
 * message or the code table (`errorMessageFa`). Never a raw code.
 */
export function failureMessage(error: unknown): string {
  const permission = missingPermission(error);
  if (permission) {
    const session = useSessionStore.getState();
    const membership = session.memberships.find((m) => m.clinicId === session.activeClinicId);
    const customRole = membership ? !isSystemRoleKey(membership.roleKey) : false;
    return permissionCopy(permission, { customRole }).reason;
  }
  return errorMessageFa(error);
}

export const toast = {
  success(content: string): void {
    api?.message.success(content);
  },
  info(content: string): void {
    api?.message.info(content);
  },
  warning(content: string): void {
    api?.message.warning(content);
  },
  /**
   * Renders whatever Persian sentence the failure carries — the server's own
   * message when there is one, the code table otherwise, and a named
   * permission for a permission refusal. Never a raw code.
   */
  error(error: unknown): void {
    noteShown(error);
    api?.message.error(failureMessage(error));
  },
  /**
   * Loud, dismissible, with a title — for a failure the user must act on. Its
   * tracking code is on it, with a copy button, as on the full-page errors.
   */
  errorBanner(title: string, error: unknown): void {
    noteShown(error);
    const message = failureMessage(error);
    const requestId = isApiError(error) ? error.requestId : undefined;
    api?.notification.error({
      message: title,
      description: requestId
        ? createElement(
            'div',
            { className: 'bk-error-banner__body' },
            createElement('div', null, message),
            createElement(TrackingCode, { value: requestId }),
          )
        : message,
    });
  },
};

export function getModalApi(): ModalHookApi | null {
  return api?.modal ?? null;
}

export function getNotificationApi(): NotificationInstance | null {
  return api?.notification ?? null;
}
